import React from 'react';
import { Button, Modal, Space, DatePicker, TimePicker } from 'antd';
import * as dayjs from 'dayjs';
import securityStyles from '../security/security.module.css';
import * as translations from './strings';
import { getLangKey } from '../../../../models/Language';
import styles from './style.module.css';
import { Calendar, SquarePen, CircleX } from 'lucide-react';
import cx from 'classnames';
import ArrowUp from '../../../settings/billing/addonPremiumIntegrations/images/ic-upgrade-circle.svg';

const strings = translations[getLangKey()];

type Props = {
  container: HTMLDivElement;
  feedbackDeadlineAllowed: boolean;
  acceptingFeedbackEndDate: string | null;
  setAcceptingFeedbackEndDate: (date: string | null) => void;
  onScheduleEndDate: (date: string) => void;
  onRemoveSchedule: () => void;
  onPauseFeedbackCollection: () => void;
  setOpen: (open: boolean) => void;
  open: boolean;
  isAcceptingFeedback: boolean;
  billing: boolean;
  organizationId: number;
  owner: { email: string } | null;
  scheduleLoading?: boolean;
};

export const FeedbackCollection = ({
  container,
  feedbackDeadlineAllowed,
  acceptingFeedbackEndDate,
  setAcceptingFeedbackEndDate,
  onScheduleEndDate,
  onRemoveSchedule,
  onPauseFeedbackCollection,
  setOpen,
  open,
  isAcceptingFeedback,
  billing,
  organizationId,
  owner,
  scheduleLoading
}: Props) => {
  const validEndDate = feedbackDeadlineAllowed && acceptingFeedbackEndDate;
  return (
    <>
      <Space direction="vertical" size={4} className={styles.feedbackDateAndButtons}>
        {validEndDate && (
          <p
            className={cx(styles.scheduledDate, {
              [styles.acceptingFeedback]: isAcceptingFeedback
            })}
          >
            {(isAcceptingFeedback
              ? strings.acceptingFeedbackUntil
              : strings.feedbackCollectionExpired) +
              dayjs(acceptingFeedbackEndDate).format('D MMMM YYYY [at] h:mm A')}
          </p>
        )}
        <Space wrap className={styles.feedbackDateActions}>
        <Button
          type="text"
          onClick={() => setOpen(true)}
          className={securityStyles.datePickerButton}
          disabled={!feedbackDeadlineAllowed}
          icon={
            validEndDate ? (
              <SquarePen className={securityStyles.datePickerButtonIcon} />
            ) : (
              <Calendar className={securityStyles.datePickerButtonIcon} />
            )
          }
        >
          {validEndDate ? strings.editSchedule : strings.scheduleTimeAndDate}
        </Button>
        {validEndDate && (
          <Button
            type="text"
            style={{ color: 'var(--colorSeverityCritical)' }}
            className={securityStyles.datePickerButton}
            icon={<CircleX className={securityStyles.datePickerButtonIcon} />}
            loading={scheduleLoading}
            onClick={onRemoveSchedule}
          >
            {strings.removeScedule}
          </Button>
        )}
        {!feedbackDeadlineAllowed && (
          <div
            className={styles.upgradeButton}
            onClick={() => {
              if (billing)
                window.open(
                  `/organizations/${organizationId}/subscription`,
                  '_self'
                );
              else if (owner) window.open(`mailto:${owner.email}`);
            }}
          >
            <ArrowUp className={styles.upgradeIcon} />
            <span className={styles.updateText}>{strings.upgrade}</span>
          </div>
        )}
        </Space>
      </Space>
      {feedbackDeadlineAllowed && (
        <Modal
          title={strings.feedbackDateModalTitle}
          open={open}
          rootClassName="antd-container"
          onCancel={() => setOpen(false)}
          footer={null}
          getContainer={container}
          centered
          width={600}
        >
          <div className={securityStyles.feedbackDateModalInner}>
            <p>{strings.feedbackDateModalMessage}</p>
            <Space direction="vertical">
              <Space className={styles.pickersWrapper}>
                <DatePicker
                  onChange={date => {
                    if (!date) return;
                    setAcceptingFeedbackEndDate(
                      date
                        .hour(17)
                        .minute(0)
                        ?.utc()
                        .toISOString()
                    );
                  }}
                  value={
                    acceptingFeedbackEndDate
                      ? dayjs(acceptingFeedbackEndDate)
                      : null
                  }
                  className={securityStyles.datePicker}
                  getPopupContainer={() => container}
                  format="DD/MM/YYYY"
                />
                <TimePicker
                  onChange={value => {
                    if (!value) return;
                    setAcceptingFeedbackEndDate(value.utc().toISOString());
                  }}
                  use12Hours
                  minuteStep={15}
                  value={
                    acceptingFeedbackEndDate
                      ? dayjs(acceptingFeedbackEndDate)
                      : null
                  }
                  format="h:mm A"
                  className={securityStyles.timePicker}
                  getPopupContainer={() => container}
                />
              </Space>
              <Space>
                <Button
                  type="primary"
                  loading={scheduleLoading}
                  onClick={() => {
                    if (!acceptingFeedbackEndDate) return;
                    onScheduleEndDate(acceptingFeedbackEndDate);
                    setOpen(false);
                  }}
                  disabled={!acceptingFeedbackEndDate}
                >
                  {strings.schedule}
                </Button>
                <Button
                  type="text"
                  loading={scheduleLoading}
                  onClick={() => {
                    onPauseFeedbackCollection();
                    setOpen(false);
                  }}
                >
                  {strings.disableFeedbackCollection}
                </Button>
              </Space>
            </Space>
          </div>
        </Modal>
      )}
    </>
  );
};
